import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function AboutPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Interior Decor Solutions',
    description: 'We believe every home deserves thoughtful touches without the hassle. Learn about our mission, values, and team.',
    url: `${siteUrl}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
      description: 'We test, curate, and recommend only products that bring real value to real homes.',
      url: siteUrl,
      foundingDate: '2024',
      founder: {
        '@type': 'Person',
        name: 'Emily Sanderwal',
        jobTitle: 'Founder & Chief Curator',
      },
      employee: [
        {
          '@type': 'Person',
          name: 'Emily Sanderwal',
          jobTitle: 'Founder & Chief Curator',
        },
        {
          '@type': 'Person',
          name: 'Marcus Johnson',
          jobTitle: 'Product Testing Lead',
        },
        {
          '@type': 'Person',
          name: 'Sofia Martinez',
          jobTitle: 'Content Director',
        },
      ],
    },
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5dbhf1dn6dhfpiac24g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const values = [
    {
      icon: 'ri-heart-line',
      title: 'Authenticity',
      description: 'We only recommend products we genuinely love and would use in our own homes. No fake reviews, no paid placements.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality First',
      description: 'Every product is thoroughly tested for quality, durability, and value before we share it with you.'
    },
    {
      icon: 'ri-user-smile-line',
      title: 'Real Results',
      description: 'Our recommendations come from real experiences in real homes, not just product descriptions.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Practical Advice',
      description: 'We focus on actionable tips and realistic solutions that work for everyday living.'
    }
  ];

  const team = [
    {
      name: 'Emily Sanderwal',
      role: 'Founder & Chief Curator',
      image: 'https://public.readdy.ai/ai/img_res/11b28d809426d409e6158b6def34bf4f.jpg',
      bio: 'Former interior designer with 10 years of experience helping homeowners create spaces they love.'
    },
    {
      name: 'Marcus Johnson',
      role: 'Product Testing Lead',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20friendly%20man%20home%20improvement%20expert%20in%20modern%20workspace%20with%20neutral%20background%20and%20natural%20lighting%20showing%20trustworthy%20professional%20aesthetic&width=400&height=400&seq=team2&orientation=squarish',
      bio: 'Quality obsessed with a background in product design and a passion for finding the best value.'
    },
    {
      name: 'Sofia Martinez',
      role: 'Content Director',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20friendly%20woman%20lifestyle%20writer%20in%20bright%20modern%20office%20with%20neutral%20background%20and%20natural%20lighting%20showing%20creative%20professional%20aesthetic&width=400&height=400&seq=team3&orientation=squarish',
      bio: 'Lifestyle writer who believes beautiful homes should be accessible to everyone, regardless of budget.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About Us - Interior Decor Solutions | Our Story & Mission"
        description="We believe every home deserves thoughtful touches without the hassle. Learn about our mission to provide honest home decor reviews, meet our team, and discover our values."
        keywords="about us, interior decor solutions, home decor team, our mission, our story, home styling experts, product testing"
        canonical={`${siteUrl}/about`}
        jsonLd={jsonLd}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#FF6B6B] font-medium text-base whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#FF6B6B] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4] mt-[70px]">
        <div className="max-w-7xl mx-auto">
          <p className="text-gray-500 text-sm mb-4">(Our Story)</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-center">
            <div>
              <h1 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 md:mb-8">
                We believe every<br />
                home deserves<br />
                thoughtful touches<br />
                without the hassle
              </h1>
              <p className="text-gray-600 text-base md:text-xl leading-relaxed">
                Simple Home Decor was born from a simple frustration: finding quality home decor shouldn't require endless hours of research and comparison shopping.
              </p>
            </div>
            <div className="h-[350px] md:h-[500px] rounded-3xl overflow-hidden shadow-lg">
              <img 
                src="https://readdy.ai/api/search-image?query=warm%20inviting%20home%20interior%20workspace%20with%20natural%20light%20showing%20comfortable%20modern%20furniture%20plants%20and%20thoughtful%20decor%20details%20in%20neutral%20tones%20with%20teal%20and%20coral%20accents%20creating%20inspiring%20atmosphere&width=800&height=800&seq=abouthero&orientation=squarish"
                alt="Our workspace"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-12 md:mb-20">
            <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8">Our Mission</h2>
            <p className="text-gray-600 text-base md:text-xl leading-relaxed mb-4 md:mb-6">
              We test, curate, and recommend only products that bring real value to real homes. Every item we feature has been personally evaluated for quality, functionality, and aesthetic appeal.
            </p>
            <p className="text-gray-600 text-base md:text-xl leading-relaxed">
              Our goal is to save you time and money by doing the research for you, so you can focus on what matters: creating a home you love.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FF6B6B]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5">
                  <i className={`${value.icon} text-2xl md:text-3xl text-[#FF6B6B]`}></i>
                </div>
                <h3 className="text-[#1A4D4D] text-lg md:text-xl font-semibold mb-2 md:mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-gray-500 text-sm mb-3">(Meet the Team)</p>
            <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">The People Behind the Picks</h2>
            <p className="text-gray-600 text-base md:text-xl max-w-3xl mx-auto leading-relaxed">
              We're a small team of home decor enthusiasts who are passionate about helping you create beautiful, functional spaces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                <div className="w-full h-[300px] md:h-[400px]">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-semibold mb-2">{member.name}</h3>
                  <p className="text-[#FF6B6B] text-sm md:text-base font-medium mb-3 md:mb-4">{member.role}</p>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">How We Work</h2>
            <p className="text-gray-600 text-base md:text-xl max-w-3xl mx-auto leading-relaxed">
              Our review process is thorough and transparent. Here's what goes into every recommendation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="bg-[#F9F7F4] rounded-3xl p-6 md:p-10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1A4D4D] rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <span className="text-white text-xl md:text-2xl font-bold">1</span>
              </div>
              <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-semibold mb-3 md:mb-4">Research & Selection</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                We scour the market for products that meet our quality standards and offer genuine value. We consider customer reviews, brand reputation, and price points.
              </p>
            </div>

            <div className="bg-[#F9F7F4] rounded-3xl p-6 md:p-10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1A4D4D] rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <span className="text-white text-xl md:text-2xl font-bold">2</span>
              </div>
              <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-semibold mb-3 md:mb-4">Hands-On Testing</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Every product is tested in real home environments. We evaluate quality, functionality, durability, and how well it integrates into everyday life.
              </p>
            </div>

            <div className="bg-[#F9F7F4] rounded-3xl p-6 md:p-10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1A4D4D] rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <span className="text-white text-xl md:text-2xl font-bold">3</span>
              </div>
              <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-semibold mb-3 md:mb-4">Honest Review</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                We write detailed, honest reviews highlighting both pros and cons. If a product doesn't meet our standards, we don't recommend it—simple as that.
              </p>
            </div>

            <div className="bg-[#F9F7F4] rounded-3xl p-6 md:p-10">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1A4D4D] rounded-xl flex items-center justify-center mb-4 md:mb-6">
                <span className="text-white text-xl md:text-2xl font-bold">4</span>
              </div>
              <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-semibold mb-3 md:mb-4">Ongoing Updates</h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                We regularly revisit our recommendations to ensure they're still the best options available. Products are removed if quality declines or better alternatives emerge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#1A4D4D]">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <pattern id="world-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                <circle cx="20" cy="30" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="80" cy="70" r="1.5" fill="white" opacity="0.2"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#world-pattern)"/>
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-8">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-3 md:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Join our community
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">
            Get weekly decor tips, exclusive deals, and honest product recommendations
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex items-center justify-center gap-0 max-w-sm mx-auto" data-readdy-form id="newsletter-about">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 h-[50px] md:h-[54px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-[50px] h-[50px] md:w-[54px] md:h-[54px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-notification-line text-lg md:text-xl"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-4 text-sm ${submitMessage.includes('Thank') ? 'text-white' : 'text-[#FF6B6B]'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-12 md:py-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-8 md:py-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
              <span className="hidden md:inline">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
